/*
 * O cartão de usuário: abre no clique do avatar ou do nome do
 * autor, junto dele, com avatar grande, nomes, grupos, bio, "visto há…",
 * "membro desde" e os botões "Ver perfil"/"Mensagem". Fecha ao clicar fora,
 * com Esc ou ao navegar.
 *
 * Há um cartão só para a página inteira, montado num <div> próprio no body:
 * dentro do post ele seria cortado pelo overflow do conteúdo e ficaria preso
 * ao SubtreeRetainer, que não redesenha o post à toa.
 *
 * O cartão aparece inteiro: ele é montado escondido e só se mostra quando o
 * perfil completo (tempo de leitura, curtidas...), os emblemas e o avatar
 * chegaram. Antes, na primeira vez depois de carregar a página, ele abria
 * com o que o post trazia e ia crescendo e mudando com o que chegava.
 */
import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Component from 'flarum/common/Component';
import Avatar from 'flarum/common/components/Avatar';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import Icon from 'flarum/common/components/Icon';
import Link from 'flarum/common/components/Link';
import Separator from 'flarum/common/components/Separator';
import classList from 'flarum/common/utils/classList';
import abbreviateNumber from 'flarum/common/utils/abbreviateNumber';
import UserControls from 'flarum/forum/utils/UserControls';
import { fullDate, shortAge, shortDate } from '../../utils/dates';
import { t } from '../../utils/settings';
import { thirdParty } from '../../thirdparty/settings';
import { Bio, BirthdayFact, LocationFact, StatusLine, WebsiteFact, profileImageStyle, userTitle } from '../../features/profile';
import { AUTHOR_FILTER_MAX_POSTS, authorPostCount, filterByAuthor } from '../authorFilter';
import { FeaturedBadges } from '../../features/badges';
import { badgesEnabled, loadUserBadges } from '../../features/badges/utils';
import { userIcons } from '../../utils/userIcons';

/** Distância do cartão até o avatar e até as bordas da janela. */
const GAP = 8;
const EDGE = 16;

/** Quanto o cartão espera pelos dados antes de abrir com o que tiver (ms). */
const MAX_WAIT = 1200;
let state = null;
let root = null;
const profiles = new Map();
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * O perfil completo e os emblemas, uma vez por sessão para cada pessoa. O
 * usuário que vem junto do post não traz as estatísticas (só a resposta do
 * perfil traz), e os emblemas vêm de outro pedido.
 */
export function prefetchUserCard(user) {
  const id = user.id();
  if (!id) return Promise.resolve();
  if (!profiles.has(id)) {
    profiles.set(id, Promise.all([app.store.find('users', id, {}, {
      errorHandler: () => {}
    }).catch(() => {}), badgesEnabled() ? loadUserBadges(user) : null]));
  }
  return profiles.get(id);
}

/** O avatar grande do cartão já baixado (ou falhou, ou não há foto). */
function avatarReady() {
  const img = root?.querySelector('.CanelaUserCard-avatar img');
  if (!img || img.complete && img.naturalWidth > 0) return Promise.resolve();
  return img.decode().catch(() => {});
}
function onDocumentEvent(event) {
  if (!state) return;
  if (event instanceof KeyboardEvent) {
    if (event.key === 'Escape') closeUserCard();
    return;
  }
  const target = event.target;
  if (root?.contains(target) || state.anchor.contains(target)) return;
  closeUserCard();
}
function ensureRoot() {
  if (root) return;
  root = document.createElement('div');
  root.className = 'CanelaUserCardRoot';
  document.body.appendChild(root);
  m.mount(root, {
    view: () => state ? m(CanelaUserCard, {
      user: state.user,
      anchor: state.anchor,
      ready: state.ready
    }) : null
  });
  document.addEventListener('mousedown', onDocumentEvent, true);
  document.addEventListener('keydown', onDocumentEvent, true);
  window.addEventListener('popstate', () => closeUserCard());

  // Um modal aberto pelo cartão (o "Editar" do ⋯, conversar…) fica por cima
  // da página: o cartão fecha, em vez de ficar sobre o modal.
  extend('flarum/common/states/ModalManagerState', 'show', () => closeUserCard());
}
export function openUserCard(user, anchor) {
  ensureRoot();

  // Um segundo clique no mesmo avatar fecha.
  if (state && state.user === user && state.anchor === anchor) {
    closeUserCard();
    return;
  }
  const current = {
    user,
    anchor,
    ready: false
  };
  state = current;

  // Montado já (escondido): o avatar começa a baixar enquanto os dados
  // chegam. Com tudo pronto, ou passado o limite, ele aparece de uma vez.
  m.redraw.sync();
  Promise.race([prefetchUserCard(user).then(() => {
    // O cartão com os dados novos, ainda escondido, antes de olhar o avatar.
    if (state === current) m.redraw.sync();
    return avatarReady();
  }), wait(MAX_WAIT)]).then(() => {
    if (state !== current) return;
    current.ready = true;
    m.redraw();
  });
}

/** O tempo de leitura (segundos) no formato curto: "35 d", "13 h", "8 min". */
function readTime(seconds) {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return app.translator.trans('ramon-canela.forum.age.minutes', {
    count: Math.max(1, minutes)
  }, true);
  if (minutes < 60 * 24) return app.translator.trans('ramon-canela.forum.age.hours', {
    count: Math.round(minutes / 60)
  }, true);
  return app.translator.trans('ramon-canela.forum.age.days', {
    count: Math.round(minutes / 60 / 24)
  }, true);
}
export function closeUserCard() {
  if (!state) return;
  state = null;
  m.redraw();
}
class CanelaUserCard extends Component {
  /**
   * O cartão (39em): o avatar de 8em subindo 3.3em
   * acima do cartão, com o flair no canto; @nome enorme e, embaixo, o nome e
   * o título; à direita "Mensagem" e "N postagens no tópico"; depois a bio
   * (2 linhas), local e site, os metadados e os emblemas. O fundo é a imagem
   * de fundo do perfil, sob um véu da cor do cartão.
   */
  view() {
    const user = this.attrs.user;
    const groups = (user.groups() || []).filter(group => group && !group.isHidden?.());
    const flairGroup = groups.find(group => group.icon());
    const lastSeen = user.lastSeenAt();
    const joined = user.joinTime();
    const timeRead = user.attribute('canelaTimeRead');
    const recentRead = user.attribute('canelaRecentTimeRead');
    const likes = user.attribute('canelaLikesReceived');
    const solutions = user.attribute('bestAnswerCount');
    // Só o título do perfil: o grupo já está no flair do avatar.
    const title = userTitle(user);
    const showName = user.displayName() !== user.username();

    // A "Mensagem" do flarum/messages vira o botão primário; o resto dos
    // controles (moderação) vai para o "…".
    const controls = UserControls.controls(user, this);
    const message = controls.has('sendMessage') ? controls.get('sendMessage') : null;
    if (message) controls.remove('sendMessage');
    // O "Chat" do ramon/chat (abrir a MD com a pessoa) vira o botão ao lado da
    // "Mensagem".
    const chat = thirdParty.ramonChatUserButton() && controls.has('chatDirect') ? controls.get('chatDirect') : null;
    if (chat) controls.remove('chatDirect');
    const otherControls = controls.toArray().filter(item => item && item.tag !== Separator);

    // "N postagens no tópico", quando o cartão abre de dentro de um tópico.
    const discussion = app.current.get('discussion');
    const stream = app.current.get('stream');
    const inTopic = discussion && stream ? authorPostCount(discussion, user) : 0;
    const facts = [LocationFact(user), WebsiteFact(user), BirthdayFact(user)].filter(Boolean);
    // Uma linha só, que quebra sozinha: com
    // Curtidas/Soluções numa segunda linha, a Curtidas ficava sozinha nos
    // fóruns sem o fof/best-answer.
    const metadata = [lastSeen && !user.isOnline() && this.meta('seen', t('topic.user_card.last_seen'), shortAge(lastSeen), fullDate(lastSeen)),
    // Online: só "online agora", em verde (o "Visto" sobra).
    user.isOnline() && m("span", {
      className: "CanelaUserCard-meta CanelaUserCard-online"
    }, m("span", {
      className: "CanelaUserCard-onlineNow"
    }, t('user.stats.online'))), joined && this.meta('joined', t('topic.user_card.joined'), shortDate(joined), fullDate(joined)),
    // "Lido 3 meses (1 d recentemente)": o tempo dos últimos 60 dias entre
    // parênteses.
    typeof timeRead === 'number' && timeRead > 0 && this.meta('read', t('topic.user_card.read'), typeof recentRead === 'number' && recentRead > 0 && recentRead < timeRead ? `${readTime(timeRead)} (${t('topic.user_card.read_recently', {
      time: readTime(recentRead)
    })})` : readTime(timeRead)), typeof likes === 'number' && this.meta('likes', t('topic.user_card.likes_received'), abbreviateNumber(likes), undefined, true), typeof solutions === 'number' && solutions > 0 && this.meta('solutions', t('topic.user_card.solutions'), abbreviateNumber(solutions))].filter(Boolean);
    const status = StatusLine(user);
    const bio = Bio(user);
    return m("div", {
      className: classList('CanelaUserCard', {
        'CanelaUserCard--ready': this.attrs.ready,
        'CanelaProfile--hasCardBg': !!profileImageStyle(user)['--canela-user-card-bg']
      }),
      "aria-hidden": this.attrs.ready ? undefined : 'true',
      role: "dialog",
      "aria-label": user.displayName(),
      style: {
        '--usercard-color': user.color() || undefined,
        ...profileImageStyle(user)
      }
    }, m("div", {
      className: "CanelaUserCard-content"
    }, m("div", {
      className: "CanelaUserCard-first"
    }, m("div", {
      className: "CanelaUserCard-avatarWrapper"
    }, m(Link, {
      className: "CanelaUserCard-avatar",
      href: app.route.user(user),
      onclick: closeUserCard
    }, m(Avatar, {
      user: user,
      loading: "eager"
    })), flairGroup && m("span", {
      className: "CanelaUserCard-flair",
      title: flairGroup.nameSingular(),
      style: {
        '--group-color': flairGroup.color() || undefined
      }
    }, m(Icon, {
      name: flairGroup.icon()
    }))), m("div", {
      className: "CanelaUserCard-names"
    }, m("div", {
      className: "CanelaUserCard-nameLine"
    }, m(Link, {
      className: "CanelaUserCard-name",
      href: app.route.user(user),
      onclick: closeUserCard
    }, m("span", {
      className: "CanelaUserCard-username"
    }, user.displayName() || user.username())), userIcons(user, 'card')), showName && m("span", {
      className: "CanelaUserCard-secondary"
    }, user.username()), title && m("span", {
      className: "CanelaUserCard-secondary CanelaUserCard-title"
    }, title)), m("div", {
      className: "CanelaUserCard-controls"
    }, chat && m(Button, Object.assign({}, chat.attrs, {
      className: "Button CanelaUserCard-chat",
      icon: "fas fa-comment-dots"
    }), t('chat.start_conversation')), message && m(Button, Object.assign({}, message.attrs, {
      className: "Button Button--primary CanelaUserCard-message",
      icon: "far fa-envelope"
    }), message.children), inTopic > 1 && discussion.postIds().length <= AUTHOR_FILTER_MAX_POSTS && m(Button, {
      className: "Button CanelaUserCard-filter",
      icon: "fas fa-filter",
      onclick: () => {
        closeUserCard();
        filterByAuthor(stream, discussion, user);
      }
    }, t('topic.user_card.posts_in_topic', {
      count: inTopic
    })), otherControls.length > 0 && m(Dropdown, {
      className: "CanelaUserCard-more",
      buttonClassName: "Button Button--icon",
      menuClassName: "Dropdown-menu--right",
      icon: "fas fa-ellipsis-h",
      caretIcon: "",
      label: "",
      helperText: null,
      accessibleToggleLabel: t('topic.user_card.more')
    }, controls.toArray()))), status && m("div", {
      className: "CanelaUserCard-row"
    }, status), bio && m("div", {
      className: "CanelaUserCard-row CanelaUserCard-bio"
    }, bio), facts.length > 0 && m("div", {
      className: "CanelaUserCard-row CanelaUserCard-facts"
    }, dotted(facts)), metadata.length > 0 && m("div", {
      className: "CanelaUserCard-row CanelaUserCard-metadata"
    }, dotted(metadata)), m(FeaturedBadges, {
      className: "CanelaUserCard-row CanelaUserCard-badges",
      user: user,
      limit: 3
    })));
  }

  /** "Rótulo valor" dos metadados do cartão. */
  meta(key, label, value, title, accent = false) {
    return m("span", {
      className: `CanelaUserCard-meta CanelaUserCard-meta--${key}`,
      title: title
    }, m("span", {
      className: "CanelaUserCard-metaLabel"
    }, label), ' ', m("span", {
      className: classList('CanelaUserCard-metaValue', {
        'CanelaUserCard-metaValue--accent': accent
      })
    }, value));
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.position();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.position();
  }

  /**
   * Ao lado do avatar clicado; se não couber embaixo, em cima; e nunca fora
   * da janela. No celular o CSS o centraliza.
   */
  position() {
    const card = this.element;
    const anchor = this.attrs.anchor;
    if (!anchor.isConnected) {
      closeUserCard();
      return;
    }
    if (app.screen() === 'phone') {
      card.style.left = '';
      card.style.top = '';
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const width = card.offsetWidth;
    const height = card.offsetHeight;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;

    // O avatar grande do cartão (que sobe 3.3em acima dele,
    // pela margem do CSS) fica à direita do avatar clicado, quase na mesma
    // altura dele.
    let left = rect.right + GAP;
    let top = rect.top - 22;
    if (left + width > viewportWidth - EDGE) left = Math.max(EDGE, viewportWidth - width - EDGE);
    if (top + height > viewportHeight - EDGE && rect.top - GAP - height > EDGE) top = rect.top - GAP - height;
    card.style.left = `${left + window.scrollX}px`;
    card.style.top = `${top + window.scrollY}px`;
  }
}

/**
 * Intercala um "•" entre os itens. O separador é um elemento próprio, irmão dos
 * itens: como pseudo-elemento dentro de um item que é link, ele herdava o
 * sublinhado e o clique do link.
 */
function dotted(items) {
  return items.flatMap((item, index) => index === 0 ? [item] : [m("span", {
    className: "CanelaUserCard-sep",
    "aria-hidden": "true"
  }, "\u2022"), item]);
}
flarum.reg.add('ramon-canela', 'forum/topic/components/UserCardPopover', { prefetchUserCard: prefetchUserCard,openUserCard: openUserCard,closeUserCard: closeUserCard, });